import { createSlice } from "@reduxjs/toolkit";
import CookieManager from "../utils/CookieManager";
import { InputHelper } from "../utils/InputHelper";
import { enmExportStatus } from "../models/enums/ExportStatus";
import { mdlPDFRendererProps } from "../models/ui-models/PDFRendererProps";
import { enmPdfExportType } from "../models/enums/PdfExportType";
import { mdlUserInfo } from "../models/domain/UserInfo";
import { mdlHoteluserPermissions } from "../models/domain/HotelUserPermissions";
import { mdlUserPermissions } from "../models/domain/UserPermissions";
import { mdlHotel } from "../models/domain/Hotel";
import { mdlIdNameIndexPair } from "../models/domain/IdNameIndexPair";
import { mdlHotelUserPair } from "../models/domain/HotelUserPair";
import { mdlRemember } from "../models/ui-models/Remember";
import _ from "lodash";

interface IinitialState {
  rememberMe: mdlRemember | undefined;
  resMonColumns: Array<string> | undefined;
  user: mdlUserInfo | undefined;
  userHotels: Array<mdlHotel> | undefined;
  crossAgencies: Array<mdlIdNameIndexPair> | undefined;
  hotelUserPairs: Array<mdlHotelUserPair> | undefined;
  agencyLimitedHotelPaximumIds: Array<number> | undefined;
  hotelUserPermissions: mdlHoteluserPermissions[];
  token: string | undefined;
  loading: boolean;
  openMobileMenu: boolean;
  isMobile: boolean;
  darkMode: boolean;
  language: string;
  pdfExportObject: mdlPDFRendererProps | undefined;
  exportStatus: enmExportStatus;
  printStatus: enmExportStatus;
}

const initialState: IinitialState = {
  rememberMe: !InputHelper.isNullOrUndefinedOrEmpty(CookieManager.get("rememberInfo"))
    ? JSON.parse(CookieManager.get("rememberInfo"))
    : undefined,
  resMonColumns: !InputHelper.isNullOrUndefinedOrEmpty(CookieManager.get("table_view"))
    ? JSON.parse(CookieManager.get("table_view"))
    : undefined,
  user: !InputHelper.isNullOrUndefinedOrEmpty(CookieManager.get("user"))
    ? JSON.parse(CookieManager.get("user"))
    : undefined,
  userHotels: !InputHelper.isNullOrUndefinedOrEmpty(sessionStorage.getItem("userHotels"))
    ? JSON.parse(sessionStorage.getItem("userHotels")!)
    : undefined,
  crossAgencies: !InputHelper.isNullOrUndefinedOrEmpty(sessionStorage.getItem("crossAgencies"))
    ? JSON.parse(sessionStorage.getItem("crossAgencies")!)
    : undefined,
  hotelUserPairs: !InputHelper.isNullOrUndefinedOrEmpty(sessionStorage.getItem("hotelUserPairs"))
    ? JSON.parse(sessionStorage.getItem("hotelUserPairs")!)
    : undefined,
  agencyLimitedHotelPaximumIds: !InputHelper.isNullOrUndefinedOrEmpty(sessionStorage.getItem("agencyLimitedHotelPaximumIds"))
    ? JSON.parse(sessionStorage.getItem("agencyLimitedHotelPaximumIds")!)
    : undefined,
  hotelUserPermissions: [],
  token: !InputHelper.isNullOrUndefinedOrEmpty(CookieManager.get("authToken"))
    ? CookieManager.get("authToken")
    : undefined,
  loading: false,
  openMobileMenu: false,
  isMobile: window.innerWidth <= 1024,
  darkMode:
    !InputHelper.isNullOrUndefinedOrEmpty(CookieManager.get("darkMode"))
      ? CookieManager.get("darkMode") === "true"
      : false,
  language:
    !InputHelper.isNullOrUndefinedOrEmpty(CookieManager.get("language"))
      ? CookieManager.get("language")
      : "en-US",
  exportStatus: enmExportStatus.DONE,
  pdfExportObject: undefined,
  printStatus: enmExportStatus.DONE,
};

const siteSlice = createSlice({
  name: "site",
  initialState,
  reducers: {
    setUser: (state = initialState, action) => {
      const persistentUserInfo: mdlUserInfo = { ...action.payload as mdlUserInfo }
      delete persistentUserInfo.token;
      delete persistentUserInfo.permissions;
      delete persistentUserInfo.hotels;
      delete persistentUserInfo.crossAgencies;
      state.user = persistentUserInfo;
      CookieManager.set("user", JSON.stringify(persistentUserInfo), 0, 6);
      return state;
    },
    setUserHotels: (state = initialState, action: { payload: Array<mdlHotel> }) => {
      const hotelList = action.payload.map((userHotel) => {
        let hotelUserPair = state.hotelUserPairs?.find((hup) => hup.hotelId === userHotel.id);

        return {
          ...userHotel,
          requirePayment: hotelUserPair?.paymentRequired,
          paymentValidityDate: hotelUserPair?.paymentValidityDate,
          daysLeftToPaymentValidityDate: hotelUserPair?.daysLeftToPaymentValidityDate
        };
      });

      state.userHotels = hotelList;
      sessionStorage.setItem("userHotels", JSON.stringify(hotelList));
      return state;
    },
    setRememberInfo: (state = initialState, action) => {
      state.rememberMe = action.payload;
      CookieManager.set("rememberInfo", JSON.stringify(action.payload));
      return state;
    },
    setCrossAgencies: (state = initialState, action) => {
      state.crossAgencies = action.payload;
      sessionStorage.setItem("crossAgencies", JSON.stringify(action.payload));
      return state;
    },
    setHotelUserPairs: (state = initialState, action) => {
      state.hotelUserPairs = action.payload;
      sessionStorage.setItem("hotelUserPairs", JSON.stringify(action.payload));
      return state;
    },
    setAgencyLimitedHotelPaximumIds: (state = initialState, action) => {
      state.agencyLimitedHotelPaximumIds = action.payload;
      sessionStorage.setItem("agencyLimitedHotelPaximumIds", JSON.stringify(action.payload));
      return state;
    },
    setHotelUserPermissions: (state = initialState, action) => {
      const persistentHoteluserPermissions: mdlHoteluserPermissions[] = [...action.payload as mdlHoteluserPermissions[]];
      persistentHoteluserPermissions.forEach((hotelUserPermissions: mdlHoteluserPermissions) => {
        hotelUserPermissions.permissions = hotelUserPermissions.permissions?.map((userPermissions) => ({ name: userPermissions.name })) as mdlUserPermissions[];
      });
      state.hotelUserPermissions = persistentHoteluserPermissions;
      CookieManager.set("hotelUserPermissions", JSON.stringify(persistentHoteluserPermissions), 0, 6);
      return state;
    },
    logout: (state) => {
      state.user = undefined;
      state.token = undefined;
      CookieManager.remove("authToken");
      CookieManager.remove("user");
      sessionStorage.removeItem("userHotels");
      CookieManager.remove("hotelUserPermissions");
    },
    setToken: (state = initialState, action) => {
      state.token = action.payload;
      CookieManager.set("authToken", state.token!, 0, 6);
      return state;
    },
    toLoading: (state) => {
      state.loading = true;
    },
    toUnLoading: (state) => {
      state.loading = false;
    },
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
      CookieManager.set("darkMode", String(state.darkMode));
    },
    openMobileMenu: (state, action) => {
      state.openMobileMenu = action.payload;
    },
    setMobile: (state, action) => {
      state.isMobile = action.payload;
    },
    setLanguage: (state, action) => {
      state.language = action.payload;
      CookieManager.set("language", state.language);
    },
    setResMonColumns: (state, action) => {
      state.resMonColumns = action.payload;
      CookieManager.set("table_view", JSON.stringify(action.payload));
    },
    setPdfExportObject: (state, action) => {
      if (action.payload != null) {
        if (action.payload.type == enmPdfExportType.PDF) {
          state.exportStatus = enmExportStatus.REQUESTED;
        }
        else {
          state.printStatus = enmExportStatus.REQUESTED;
        }
        state.pdfExportObject = action.payload;
      }
      else {
        state.exportStatus = enmExportStatus.DONE;
        state.printStatus = enmExportStatus.DONE;
        state.pdfExportObject = undefined;
      }
      return state;
    },
  },
});

export const {
  setUser,
  toLoading,
  toUnLoading,
  openMobileMenu,
  setMobile,
  setToken,
  toggleDarkMode,
  logout,
  setLanguage,
  setPdfExportObject,
  setUserHotels,
  setCrossAgencies,
  setResMonColumns,
  setHotelUserPermissions,
  setHotelUserPairs,
  setAgencyLimitedHotelPaximumIds,
  setRememberInfo
} = siteSlice.actions;
export default siteSlice.reducer;
